import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import { errorMessage, useTRPC } from "../../lib/api";
import { useMe } from "../../lib/me";
import { Button, ErrorText, PageHeader, Section, StatusText, TextField } from "../../ui";
import { InvitePersonForm } from "./InvitePersonForm";
import { SettingsNav } from "./SettingsNav";

export function WorkspacesPage() {
  const me = useMe();
  return (
    <div className="flex flex-col gap-6">
      <PageHeader title="Workspaces" />
      <SettingsNav />      <Section title="Your workspaces">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="text-xs uppercase tracking-wide text-muted">
              <tr>
                <th scope="col" className="py-2 pr-4 font-medium">
                  Name
                </th>
                <th scope="col" className="py-2 font-medium">
                  Your role
                </th>
              </tr>
            </thead>
            <tbody>
              {me.workspaces.map((w) => (
                <tr key={w.id} className="border-t border-border">
                  <td className="py-2 pr-4">
                    <Link
                      to="/settings/workspaces/$workspaceId"
                      params={{ workspaceId: w.id }}
                      className="font-medium text-accent underline-offset-2 hover:underline"
                    >
                      {w.name}
                    </Link>
                  </td>
                  <td className="py-2 text-muted">{w.role === "owner" ? "Owner" : "Member"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>
      <CreateWorkspaceForm />
      {me.isAdmin ? <InvitePersonForm /> : null}
    </div>
  );
}

function CreateWorkspaceForm() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [created, setCreated] = useState<string | null>(null);
  const create = useMutation(
    trpc.workspaces.create.mutationOptions({
      onSuccess: async (ws) => {
        setCreated(ws.name);
        setName("");
        await queryClient.invalidateQueries({ queryKey: trpc.me.queryKey() });
      },
    }),
  );

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setCreated(null);
    create.mutate({ name: name.trim() });
  }

  return (
    <Section title="Create a workspace" description="You will be its owner and can add members who have signed in.">
      <form onSubmit={onSubmit} className="flex flex-col gap-3 sm:flex-row sm:items-end">
        <TextField
          label="Workspace name"
          name="name"
          required
          maxLength={60}
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="flex-1"
        />
        <Button type="submit" variant="primary" disabled={create.isPending || name.trim().length === 0}>
          {create.isPending ? "Creating…" : "Create workspace"}
        </Button>
      </form>
      <div className="mt-2 min-h-5">
        {create.isError ? <ErrorText>{errorMessage(create.error)}</ErrorText> : null}
        {created === null ? null : <StatusText>Created {created}.</StatusText>}
      </div>
    </Section>
  );
}
